import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import type { Brand } from '@hygh-vybe/lib';
import { API_BASE } from '../../config';

export function AdminBrands() {
  const { authFetch } = useAuth();
  const [searchParams] = useSearchParams();
  const [brands, setBrands] = useState<Brand[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [filterAffiliate, setFilterAffiliate] = useState(searchParams.get('filter') === 'affiliates');
  const [filterFeatured, setFilterFeatured] = useState(searchParams.get('filter') === 'featured');
  const [filterBuyable, setFilterBuyable] = useState<'all' | 'yes' | 'no'>('all');

  useEffect(() => {
    async function fetchBrands() {
      try {
        const res = await authFetch(`${API_BASE}/v1/brands`);
        const json = await res.json();
        if (res.ok && json.data) {
          setBrands(Array.isArray(json.data) ? json.data : []);
        } else {
          setError(json.error?.message || 'Failed to load brands');
        }
      } catch {
        setError('Failed to load brands');
      } finally {
        setLoading(false);
      }
    }

    fetchBrands();
  }, [authFetch]);

  // Client-side filtering
  const filtered = brands.filter((brand) => {
    if (filterAffiliate && !brand.affiliateNetwork) return false;
    if (filterFeatured && !brand.featured) return false;
    if (filterBuyable === 'yes' && !brand.allProductsBuyable) return false;
    if (filterBuyable === 'no' && brand.allProductsBuyable) return false;
    if (search) {
      const q = search.toLowerCase();
      if (!brand.brandName.toLowerCase().includes(q)) return false;
    }
    return true;
  });

  if (loading) {
    return <div className="admin-loading">Loading brands...</div>;
  }

  return (
    <div style={{ width: '90%', margin: '0 auto' }}>
      <div className="admin-top-bar">
        <h1>Brands {filtered.length !== brands.length && <span style={{ fontSize: '0.875rem', fontWeight: 400, color: 'var(--color-text-muted)' }}>({filtered.length} of {brands.length})</span>}</h1>
        <Link to="/admin/brands/new" className="admin-btn admin-btn-primary">
          + Add Brand
        </Link>
      </div>

      <div className="admin-search-bar">
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search brands..."
          className="admin-search-input"
        />
        <button
          type="button"
          className={`admin-btn ${filterAffiliate ? 'admin-btn-primary' : 'admin-btn-secondary'}`}
          onClick={() => { setFilterAffiliate(!filterAffiliate); setFilterFeatured(false); }}
        >
          {filterAffiliate ? '✓ Affiliates' : 'Affiliates'}
        </button>
        <button
          type="button"
          className={`admin-btn ${filterFeatured ? 'admin-btn-primary' : 'admin-btn-secondary'}`}
          onClick={() => { setFilterFeatured(!filterFeatured); setFilterAffiliate(false); }}
        >
          {filterFeatured ? '✓ Featured' : 'Featured'}
        </button>
        <select
          value={filterBuyable}
          onChange={(e) => setFilterBuyable(e.target.value as 'all' | 'yes' | 'no')}
          style={{ padding: '6px 10px', borderRadius: '4px', border: '1px solid var(--color-border)', fontSize: '0.85rem' }}
        >
          <option value="all">Buyable: All</option>
          <option value="yes">Buyable: Yes</option>
          <option value="no">Buyable: No</option>
        </select>
        {(search || filterAffiliate || filterFeatured || filterBuyable !== 'all') && (
          <button
            type="button"
            className="admin-btn admin-btn-secondary"
            onClick={() => { setSearch(''); setFilterAffiliate(false); setFilterFeatured(false); setFilterBuyable('all'); }}
          >
            Clear
          </button>
        )}
      </div>

      {error && <div className="admin-alert admin-alert-error">{error}</div>}

      {filtered.length === 0 && !error ? (
        <div className="admin-placeholder">
          <p>{search || filterAffiliate || filterFeatured || filterBuyable !== 'all' ? 'No brands match your filters.' : 'No brands yet. Add your first brand to get started.'}</p>
        </div>
      ) : (
        <div className="admin-table-wrapper">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Brand</th>
                <th>Buy Website URL</th>
                <th>Contact Email</th>
                <th>Buyable</th>
                <th>Products</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((brand) => (
                <tr key={brand.brandId}>
                  <td>
                    <div>
                      <strong>{brand.brandName}</strong>
                      <div style={{ display: 'flex', gap: '4px', marginTop: '4px' }}>
                        <Link
                          to={`/admin/products?brand=${encodeURIComponent(brand.brandName)}`}
                          className="admin-btn admin-btn-secondary admin-btn-sm"
                        >
                          Products
                        </Link>
                        <a
                          href={`/thc-drinks?brand=${encodeURIComponent(brand.brandName)}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="admin-btn admin-btn-secondary admin-btn-sm"
                        >
                          View
                        </a>
                        <Link
                          to={`/admin/brands/${brand.brandId}/edit`}
                          className="admin-btn admin-btn-secondary admin-btn-sm"
                        >
                          Edit
                        </Link>
                      </div>
                    </div>
                  </td>
                  <td style={{ fontSize: '0.8rem' }}>
                    {brand.buyWebsiteUrl ? (
                      <a href={brand.buyWebsiteUrl} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--color-primary)' }}>
                        {brand.buyWebsiteUrl.replace(/^https?:\/\/(www\.)?/, '').slice(0, 35)}
                      </a>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td style={{ fontSize: '0.8rem' }}>{brand.contactEmail || '—'}</td>
                  <td>
                    <span className={`admin-badge ${brand.allProductsBuyable ? 'admin-badge-green' : 'admin-badge-yellow'}`}>
                      {brand.allProductsBuyable ? 'Yes' : 'No'}
                    </span>
                  </td>
                  <td>{brand.productCount || 0}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
